import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {FiGrid, FiBarChart2, FiUsers, FiFeather, FiAlertTriangle,
  FiVideo, FiCalendar, FiSettings, FiBell,
  FiLogOut, FiChevronLeft, FiChevronRight, FiMessageSquare,
} from "react-icons/fi";
import { MdOutlineSoupKitchen } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../utils/auth";
import adminApi from "../../services/adminApi";
import useAdminProfile from "../../hooks/useAdminProfile";
import packageJson from "../../../package.json";
// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  olive: "#6B8A5A", olive2: "#8BA47A",
  orange: "#F29A0C", orange2: "#E97818",
  border: "rgba(239,229,216,0.8)",
  green: "#22C55E",
};

// ─── ORBITAL LOGO (Dynamic Logo & Favicon Listener) ───────────────────────────
function OrbitalLogo({ compact = false }) {
  const [logoSrc, setLogoSrc] = useState(() => {
    try {
      const saved = localStorage.getItem("divyayoga_appearance");
      const parsed = saved ? JSON.parse(saved) : null;
      return parsed?.logoUrl || "/assets/logo.jpeg";
    } catch {
      return "/assets/logo.jpeg";
    }
  });

  useEffect(() => {
    const handleAppearanceUpdate = (e) => {
      let data = e?.detail;
      if (!data) {
        try { data = JSON.parse(localStorage.getItem("divyayoga_appearance") || "{}"); } catch { data = {}; }
      }
      if (data?.logoUrl) {
        setLogoSrc(data.logoUrl);
      } else if (data?.logoUrl === null) {
        setLogoSrc("/assets/logo.jpeg");
      }

      // Live Favicon Tab Icon Update
      const targetFavicon = data?.faviconUrl || data?.logoUrl;
      if (targetFavicon) {
        let link = document.querySelector("link[rel*='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "shortcut icon";
          document.getElementsByTagName("head")[0].appendChild(link);
        }
        link.href = targetFavicon;
      }
    };

    window.addEventListener("divyayoga-appearance-updated", handleAppearanceUpdate);
    window.addEventListener("storage", handleAppearanceUpdate);
    handleAppearanceUpdate();

    return () => {
      window.removeEventListener("divyayoga-appearance-updated", handleAppearanceUpdate);
      window.removeEventListener("storage", handleAppearanceUpdate);
    };
  }, []);

  const sizeClass = compact ? "w-[44px] h-[44px]" : "w-[48px] h-[48px]";
  return (
    <div className={`relative ${sizeClass} flex-shrink-0`}>
      {/* Outer orbit dot */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0"
      >
        <div
          className="absolute w-[5px] h-[5px] rounded-full bg-white/70"
          style={{ top: 2, left: "50%", transform: "translateX(-50%)" }}
        />
      </motion.div>
      {/* Inner orbit dot */}
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        className="absolute inset-[6px]"
      >
        <div
          className="absolute w-[4px] h-[4px] rounded-full bg-white/40"
          style={{ top: 0, left: "50%", transform: "translateX(-50%) translateY(-50%)" }}
        />
      </motion.div>
      {/* Core */}
      <motion.div
        animate={{
          boxShadow: [
            "0 0 0 0 rgba(107,138,90,0)",
            "0 0 0 6px rgba(107,138,90,0.18)",
            "0 0 0 0 rgba(107,138,90,0)",
          ],
        }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 rounded-[16px] flex items-center justify-center overflow-hidden border border-white/20 shadow-lg bg-white"
      >
        <img src={logoSrc} alt="Divyyog Foundation Logo" className="w-full h-full object-cover rounded-[16px]" />
      </motion.div>
    </div>
  );
}

// ─── RIPPLE HOOK ──────────────────────────────────────────────────────────────
function useRipple() {
  const [ripples, setRipples] = useState([]);
  const trigger = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const id = Date.now();
    setRipples((r) => [...r, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setRipples((r) => r.filter((rp) => rp.id !== id)), 600);
  };
  return { ripples, trigger };
}

// ─── NAV ITEM ─────────────────────────────────────────────────────────────────
function NavItem({ item, expanded, isActive, onClick }) {
  const { ripples, trigger } = useRipple();
  const isOlive = item.activeType === "olive";

  const activeGrad = isOlive
    ? `linear-gradient(135deg,${T.olive},${T.olive2})`
    : `linear-gradient(135deg,${T.orange},${T.orange2})`;
  const activeShadow = isOlive
    ? "0 10px 28px rgba(107,138,90,0.38)"
    : "0 10px 28px rgba(242,154,12,0.38)";

  const Icon = item.icon;

  return (
    <motion.div
      onClick={(e) => { trigger(e); onClick(item.id); }}
      whileHover={{ x: isActive ? 0 : 5 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className="relative h-[50px] flex items-center rounded-[16px] cursor-pointer overflow-hidden select-none mb-0.5"
      style={isActive ? { background: activeGrad, boxShadow: activeShadow } : {}}
    >
      {/* Hover bg */}
      {!isActive && (
        <>
          <motion.div
            className="absolute inset-0 rounded-[16px]"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            style={{ background: "rgba(107,138,90,0.07)" }}
          />
          {/* Left accent bar */}
          <motion.div
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-[3px]"
            initial={{ height: 0 }}
            whileHover={{ height: "55%" }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            style={{ background: `linear-gradient(180deg,${T.olive},${T.olive2})` }}
          />
        </>
      )}

      {/* Ripple */}
      {ripples.map((rp) => (
        <motion.span
          key={rp.id}
          className="absolute rounded-full pointer-events-none"
          style={{ left: rp.x - 30, top: rp.y - 30, width: 60, height: 60, background: "rgba(255,255,255,0.25)" }}
          initial={{ scale: 0, opacity: 0.7 }}
          animate={{ scale: 3.5, opacity: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        />
      ))}

      {/* Icon */}
      <div className="w-[50px] h-[50px] flex items-center justify-center flex-shrink-0">
        <motion.span
          whileHover={!isActive ? { scale: 1.15, rotate: -6 } : {}}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
          className="text-[20px]"
          style={{ color: isActive ? "#fff" : "#888" }}
        >
          <Icon />
        </motion.span>
      </div>

      {/* Label */}
      <AnimatePresence>
        {expanded && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
            className="text-[13px] font-semibold whitespace-nowrap flex-1 min-w-0"
            style={{
              color: isActive ? "#fff" : "#555",
              fontFamily: "'Inter',sans-serif",
              letterSpacing: "-0.2px",
            }}
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Badge */}
      {item.badge && (
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.2, delay: 0.06 }}
              className="mr-3 px-2 h-[20px] rounded-[7px] flex items-center text-[10px] font-black flex-shrink-0"
              style={
                isActive
                  ? { background: "rgba(255,255,255,0.25)", color: "#fff" }
                  : item.badgeRed
                  ? { background: "rgba(226,75,74,0.12)", color: "#E24B4A" }
                  : item.badgeGreen
                  ? { background: "rgba(34,197,94,0.12)", color: "#16A34A" }
                  : item.badgeAmber
                  ? { background: "rgba(242,154,12,0.12)", color: "#C8660A" }
                  : { background: "rgba(107,138,90,0.12)", color: "#27500A" }
              }
            >
              {item.badge}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Tooltip (collapsed only) */}
      {!expanded && (
        <div
          className="absolute left-[calc(100%+14px)] z-50 pointer-events-none opacity-0 group-hover:opacity-100
                     bg-[#1F1F1F] text-white text-[12px] font-semibold px-3 py-1.5 rounded-[10px] whitespace-nowrap shadow-lg"
          style={{ fontFamily: "'Inter',sans-serif" }}
        >
          {item.label}
          <div
            className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-full"
            style={{ borderWidth: 5, borderStyle: "solid", borderColor: "transparent #1F1F1F transparent transparent" }}
          />
        </div>
      )}
    </motion.div>
  );
}

// ─── ADMIN SIDEBAR ────────────────────────────────────────────────────────────
export default function AdminSidebar({ expanded, setExpanded, activeId, setActiveId }) {
  const [pinned, setPinned] = useState(false);
  const adminProfile = useAdminProfile();
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [adminProfile.imageUrl]);
  const navigate = useNavigate();
  const location = useLocation();

  const [counts, setCounts] = useState({
    users: 0,
    subs: 0,
    priority: 0,
    sessions: 0,
    queries: 0
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await adminApi.getDashboardStats();
        if (res.success && res.data) {
          const raw = res.data;
          const totalUsers = raw.totalUsers ?? raw.TotalUsers ?? 0;
          const activeSubs = raw.activeSubscriptions ?? raw.ActiveSubscriptions ?? 0;
          const liveSessions = raw.totalLiveSessions ?? raw.TotalLiveSessions ?? 0;
          const pendingQueries = raw.pendingQueriesCount ?? raw.PendingQueriesCount ?? 0;
          const pa = raw.priorityAlerts || raw.PriorityAlerts || {};
          const priorityTotal = pa.total ?? pa.Total ?? 0;
          setCounts(prev => ({
            ...prev,
            users: totalUsers,
            subs: activeSubs,
            priority: priorityTotal,
            sessions: liveSessions,
            queries: pendingQueries
          }));
        }
      } catch (err) {
        console.warn("Failed to load admin sidebar counts:", err);
      }
    };
    fetchCounts();
  }, []);

  const NAV_GROUPS = useMemo(() => [
    {
      label: "Overview",
      items: [
        { id: "dashboard",  icon: FiGrid, path: "/admin/dashboard",     label: "Dashboard",       activeType: "olive"  },
        { id: "analytics",  icon: FiBarChart2,  path: "/admin/reports&analytics",    label: "Analytics",       activeType: "orange" },
      ],
    },
    {
      label: "Users",
      items: [
        { id: "users",    icon: FiUsers,  path: "/admin/usermanagement",       label: "User Management", activeType: "orange", badge: counts.users ? counts.users.toLocaleString() : null },
        { id: "queries",  icon: FiMessageSquare, path: "/admin/queries",       label: "User Queries",    activeType: "orange", badge: counts.queries ? counts.queries.toString() : null, badgeAmber: true },
        { id: "subs",     icon: FiFeather,   path: "/admin/subscription",       label: "Subscriptions",   activeType: "orange", badge: counts.subs ? counts.subs.toString() : null,   badgeRed:   true },
        { id: "priority", icon: FiAlertTriangle, path:"/admin/highpriorityuser", label: "High Priority",   activeType: "orange", badge: counts.priority ? counts.priority.toString() : null,    badgeAmber: true },
      ],
    },
    {
      label: "Content",
      items: [
        { id: "content",  icon: FiVideo, path: "/admin/contentmanager",   label: "Content Manager", activeType: "olive" },
        { id: "sessions", icon: FiCalendar, path: "/admin/livesession", label: "Live Sessions",   activeType: "olive", badge: counts.sessions ? counts.sessions.toString() : null, badgeGreen: true },
        { id: "diet",     icon: MdOutlineSoupKitchen, path: "/admin/dietplans", label: "Diet Plans", activeType: "olive" },
      ],
    },
    {
      label: "System",
      items: [
        { id: "notifs",   icon: FiBell, path:"/admin/notifications",    label: "Notifications", activeType: "orange" },
        { id: "settings", icon: FiSettings, path:"/admin/settings", label: "Settings",      activeType: "orange" },
      ],
    },
  ], [counts]);

  const handleLogout = () => {
  logout();
  navigate("/login", { replace: true });
};

  const hoverTimeout = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!pinned) setExpanded(true);
  };

  const handleMouseLeave = () => {
    if (!pinned) {
      hoverTimeout.current = setTimeout(() => setExpanded(false), 220);
    }
  };

  const handleToggle = () => {
    const next = !pinned;
    setPinned(next);
    setExpanded(next);
  };

  return (
    <motion.aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      animate={{ width: expanded ? 280 : 80 }}
      transition={{ duration: 0.42, ease: [0.25, 1, 0.5, 1] }}
      className="hidden md:flex fixed left-0 top-0 bottom-0 z-50 flex-col overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(40px)",
        WebkitBackdropFilter: "blur(40px)",
        border: "1px solid rgba(255,255,255,0.95)",
        borderRadius: "0 28px 28px 0",
        boxShadow: "4px 0 40px rgba(0,0,0,0.06), inset -1px 0 0 rgba(255,255,255,0.8)",
      }}
    >
      {/* Ambient orbs */}
      <motion.div
        animate={{ y: [0, -18, 0], x: [0, 14, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-16 -left-16 w-48 h-48 rounded-full pointer-events-none"
        style={{ background: "rgba(107,138,90,0.14)", filter: "blur(50px)" }}
      />
      <motion.div
        animate={{ y: [0, 16, 0], x: [0, -10, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: "rgba(242,154,12,0.10)", filter: "blur(40px)" }}
      />

      {/* Dot-grid watermark */}
      <div
        className="absolute inset-0 pointer-events-none rounded-[inherit] opacity-[0.035]"
        style={{
          backgroundImage: "radial-gradient(circle,#000 1px,transparent 0)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* Pin / toggle button */}
      {expanded && (
      <motion.button
        onClick={handleToggle}
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.9 }}
        className="absolute right-3.5 top-6 z-10 w-7 h-7 rounded-full flex items-center justify-center text-white"
        style={{
          background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
          border: "2.5px solid #FFF8EE",
          boxShadow: "0 4px 14px rgba(107,138,90,0.45)",
        }}
        title={pinned ? "Unpin sidebar" : "Pin sidebar open"}
      >
        {pinned ? <FiChevronLeft size={12} /> : <FiChevronRight size={12} />}
      </motion.button>
      )}
      
      {/* ── LOGO ── */}
      <div className="px-2 pt-5 pb-0 flex-shrink-0">
        <div className={`flex items-center gap-3 h-[56px] ${expanded ? 'justify-start px-1.5' : 'justify-center'}`}>
          <OrbitalLogo compact={!expanded} />
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -14 }}
                transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
                className="overflow-hidden whitespace-nowrap"
              >
                <h2
                  className="font-black text-[15px] text-[#1F1F1F] leading-tight tracking-[0.8px]"
                  style={{ fontFamily: "'Poppins',sans-serif" }}
                >
                  DIVYYOG
                </h2>
                <p
                  className="text-[9px] font-bold tracking-[1.5px] uppercase mt-0.5"
                  style={{ color: T.olive }}
                >
                  Admin Panel
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Divider */}
      <div
        className="mx-3.5 my-3 h-px flex-shrink-0"
        style={{ background: "rgba(107,138,90,0.2)" }}
      />

      {/* ── NAV ── */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden px-2.5"
        style={{ scrollbarWidth: "none" }}
      >
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.label} className={gi > 0 ? "mt-3" : ""}>
            <AnimatePresence>
              {expanded && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="text-[9px] font-black uppercase tracking-[1.8px] px-2.5 pt-2 pb-1.5"
                  style={{ color: "rgba(107,138,90,0.55)", fontFamily: "'Inter',sans-serif" }}
                >
                  {group.label}
                </motion.p>
              )}
            </AnimatePresence>
            {group.items.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                expanded={expanded}
                isActive={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Divider */}
      <div
        className="mx-3.5 my-2 h-px flex-shrink-0"
        style={{ background: "rgba(107,138,90,0.2)" }}
      />

      {/* ── FOOTER / USER ── */}
      <div className="px-2 pb-3 flex-shrink-0">
        <motion.div
          whileHover={{ y: -2 }}
          className={`rounded-[20px] cursor-pointer overflow-hidden transition-all duration-200 ${expanded ? 'p-3' : 'p-1.5 flex flex-col items-center'}`}
          style={{
            background: "rgba(107,138,90,0.08)",
            border: "1px solid rgba(107,138,90,0.18)",
            transition: "box-shadow 0.25s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.boxShadow = "0 8px 24px rgba(107,138,90,0.18)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
        >
          <div className={`flex items-center gap-2.5 ${expanded ? 'justify-start w-full' : 'justify-center'}`}>
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div
                className={`${expanded ? 'w-[44px] h-[44px]' : 'w-[38px] h-[38px]'} rounded-[14px] flex items-center justify-center text-white text-[12px] font-black overflow-hidden`}
                style={{
                  background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                  boxShadow: "0 6px 16px rgba(107,138,90,0.38)",
                  fontFamily: "'Poppins',sans-serif",
                  position: "relative",
                }}
              >
                {adminProfile.imageUrl && !avatarFailed ? (
                  <img
                    src={adminProfile.imageUrl}
                    alt={adminProfile.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <>
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(135deg,rgba(255,255,255,0.2),transparent)" }}
                    />
                    <span className="relative z-10">{adminProfile.initials}</span>
                  </>
                )}
              </div>
              <div
                className="absolute bottom-[0px] right-[0px] w-[9px] h-[9px] rounded-full border-2 z-20"
                style={{ background: T.green, borderColor: "rgba(255,248,238,0.9)" }}
              />
            </div>

            {/* User info */}
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 min-w-0 overflow-hidden whitespace-nowrap"
                >
                  <p
                    className="text-[13px] font-bold text-[#1F1F1F] truncate"
                    style={{ fontFamily: "'Inter',sans-serif" }}
                  >
                    {adminProfile.name}
                  </p>
                  <p className="text-[11px] font-semibold truncate" style={{ color: T.olive }}>
                    {adminProfile.role}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Logout button */}
          <motion.button
            onClick={handleLogout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className="mt-2.5 w-full h-[38px] rounded-[13px] flex items-center justify-center gap-2 text-[12px] font-semibold"
            style={{
              background: "rgba(255,255,255,0.85)",
              border: "none",
              cursor: "pointer",
              fontFamily: "'Inter',sans-serif",
              color: T.olive,
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.85)")}
          >
            <FiLogOut size={14} />
            <AnimatePresence>
              {expanded && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </motion.div>

        {/* Version / Collapsed hint */}
        <AnimatePresence>
          {expanded ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center mt-2.5 flex items-center justify-center gap-1.5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-[10.5px] font-semibold tracking-wider text-[#6B8A5A]" style={{ fontFamily: "'Inter',sans-serif" }}>
                Divyyog v{packageJson.version}
              </span>
            </motion.div>
          ) : (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center text-[9px] font-bold uppercase tracking-[1px] mt-2 text-[#6B8A5A]/80"
              style={{ fontFamily: "'Inter',sans-serif" }}
            >
              v{packageJson.version}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.aside>
  );
}